/**
 * Carrega o widget do 4uChat e o mantém em dia com quem está logado.
 */

import type { PaletteOptions } from "@mui/material";
import i18n from "../i18n";
import { Extension } from "../models/intalled-extensions";
import { getChatLinkParams, type IChatLinkParams } from "./chatLink.service";
import store from "../store/store";
import { getIndividualNumber } from "./individual.service";

const WIDGET_URL = process.env.REACT_APP_CHAT_WIDGET_URL?.trim();
const SCRIPT_ID = "4uchat-widget-script";

type TChatIdentity = {
  name?: string;
  email?: string;
  phone?: string;
} | null;

type TChatColors = {
  primary?: string;
  secondary?: string;
  mode?: "light" | "dark";
} | null;

type TChatCommand =
  | ["setIdentity", TChatIdentity]
  | ["setLocale", string | null]
  | ["setColors", TChatColors];

declare global {
  interface Window {
    fourUHubChatConfig?: {
      colors?: {
        primary?: string;
        secondary?: string;
        mode?: "light" | "dark";
      };
      locale?: string;
      ticketServiceId?: string;
      fullScreenChat?: boolean;
      originTicketId?: string;
      initialMessage?: string;
      userName?: string;
      userEmail?: string;
      userPhone?: string;
      title?: string;
      subtitle?: string;
      botName?: string;
      logoUrl?: string;
      botAvatarUrl?: string;
      width?: number;
      height?: number;
    };
    fourUHubChatQueue?:
      | TChatCommand[]
      | { push(command: TChatCommand): unknown };
  }
}

let linkParams: IChatLinkParams = {};

/** Telefone principal do cadastro de quem está logado (o login não traz o telefone). */
let loggedPhone: { individualId: string; phone?: string } | null = null;

function syncLoggedPhone(): void {
  const individualId = store.getState().user.individual?.id;

  if (!individualId) {
    loggedPhone = null;
    return;
  }

  if (loggedPhone?.individualId === individualId) return;
  loggedPhone = { individualId };

  getIndividualNumber({ IndividualId: individualId })
    .then(({ data }) => {
      // Trocou de usuário enquanto a resposta vinha: descarta.
      if (loggedPhone?.individualId !== individualId) return;
      loggedPhone = { individualId, phone: data?.value || undefined };
      syncIdentity();
    })
    .catch(() => {
      // Sem telefone no cadastro: o chat segue só com nome e e-mail.
    });
}

function currentIdentity(): TChatIdentity {
  const { id, fullName, email } = store.getState().user;
  const isLogged = !!id;

  const name =
    linkParams.userName ?? (isLogged ? fullName?.trim() || undefined : undefined);

  if (!name) return null;

  return {
    name,
    email: isLogged ? email?.trim() || undefined : undefined,
    phone:
      linkParams.phoneNumber ?? (isLogged ? loggedPhone?.phone : undefined),
  };
}

function mainColor(color: PaletteOptions["primary"]): string | undefined {
  if (color && typeof color === "object" && "main" in color) {
    return color.main;
  }

  return undefined;
}

function currentColors(): TChatColors {
  const { mode, palette, darkPalette } = store.getState().theme;

  const active = mode === "2" && darkPalette ? darkPalette : palette;
  const primary = mainColor(active?.primary);
  const secondary = mainColor(active?.secondary);

  return {
    ...(primary ? { primary } : {}),
    ...(secondary ? { secondary } : {}),
    mode: mode === "2" ? "dark" : "light",
  };
}

let injected = false;

function ensureWidgetLoaded(): void {
  if (injected || !WIDGET_URL) return;

  if (document.getElementById(SCRIPT_ID)) {
    injected = true;
    return;
  }

  const state = store.getState();

  const hasChatExtension = state.installedExtensions.extensions.some(
    (extension) => extension.code === Extension.FOUR_U_CHAT
  );

  const { ticketServiceId, fullScreenMode, profile } = state.chatWidget;

  // Sem extensão instalada ou sem serviço de tickets, o site não tem chat.
  if (!hasChatExtension || !ticketServiceId) return;

  const identity = currentIdentity();

  // A config precisa existir ANTES do script rodar: o widget lê na carga.
  const colors = currentColors();
  lastColors = JSON.stringify(colors);

  window.fourUHubChatConfig = {
    ...(colors ? { colors } : {}),
    ...profile,
    locale: i18n.language,
    ticketServiceId,
    fullScreenChat: linkParams.fullScreenChat ?? fullScreenMode,
    ...(linkParams.ticketId ? { originTicketId: linkParams.ticketId } : {}),
    ...(linkParams.initialMessage
      ? { initialMessage: linkParams.initialMessage }
      : {}),
    ...(identity?.name ? { userName: identity.name } : {}),
    ...(identity?.email ? { userEmail: identity.email } : {}),
    ...(identity?.phone ? { userPhone: identity.phone } : {}),
  };

  const script = document.createElement("script");
  script.id = SCRIPT_ID;
  script.src = WIDGET_URL;
  script.async = true;
  document.body.appendChild(script);

  injected = true;
}

let lastSent: string | undefined;

function syncIdentity(): void {
  const identity = currentIdentity();

  const serialized = JSON.stringify(identity);
  if (serialized === lastSent) return;
  lastSent = serialized;

  const queue = (window.fourUHubChatQueue =
    window.fourUHubChatQueue ?? ([] as TChatCommand[]));

  queue.push(["setIdentity", identity]);
}

/**
 * Mantém o chat no mesmo idioma do portal.
 */
let lastLocale: string | undefined;

function syncLocale(): void {
  const locale = i18n.language;
  if (locale === lastLocale) return;
  lastLocale = locale;

  const queue = (window.fourUHubChatQueue =
    window.fourUHubChatQueue ?? ([] as TChatCommand[]));

  queue.push(["setLocale", locale]);
}

/**
 * Mantém o chat com as cores do tema ativo (claro/escuro) sem recarregar.
 */
let lastColors: string | undefined;

function syncColors(): void {
  if (!injected) return;

  const colors = currentColors();

  const serialized = JSON.stringify(colors);
  if (serialized === lastColors) return;
  lastColors = serialized;

  const queue = (window.fourUHubChatQueue =
    window.fourUHubChatQueue ?? ([] as TChatCommand[]));

  queue.push(["setColors", colors]);
}

/** Chamada uma vez, na subida do portal. */
export const startChatWidget = () => {
  // Depois do captureChatLinkParams() — ver a nota em `linkParams`.
  linkParams = getChatLinkParams();

  const tick = () => {
    syncLoggedPhone();
    ensureWidgetLoaded();
    syncIdentity();
    syncColors();
  };

  tick();
  store.subscribe(tick);
  i18n.on("languageChanged", syncLocale);
};
